/**
 * UK Formatting Utilities
 * 
 * British formatting standards for dates, currency, and terminology
 * as specified in the Collaboration Constitution
 */

/**
 * Format a date to UK format (DD/MM/YYYY)
 */
export const formatUKDate = (date: Date | string | null | undefined): string => {
  if (!date) return '-';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return '-';
  
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Format a date and time to UK format (DD/MM/YYYY HH:MM)
 */
export const formatUKDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return '-';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return '-';
  
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Format currency as GBP (£)
 */
export const formatGBP = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(num)) return '-';
  
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(num);
};

/**
 * Format currency as GBP without decimal places (for whole pounds)
 */
export const formatGBPWhole = (amount: number | string | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(num)) return '-';
  
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

/**
 * Format a number with UK thousands separator
 */
export const formatUKNumber = (num: number | string | null | undefined): string => {
  if (num === null || num === undefined) return '-';
  
  const n = typeof num === 'string' ? parseFloat(num) : num;
  
  if (isNaN(n)) return '-';
  
  return new Intl.NumberFormat('en-GB').format(n);
};

/**
 * Format a UK phone number
 */
export const formatUKPhone = (phone: string | null | undefined): string => {
  if (!phone) return '-';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Format as UK phone number
  if (digits.startsWith('44')) {
    // International format
    return `+44 ${digits.slice(2, 6)} ${digits.slice(6)}`;
  } else if (digits.startsWith('0')) {
    // Domestic format
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  
  return phone;
};

/**
 * Format a UK postcode
 */
export const formatUKPostcode = (postcode: string | null | undefined): string => {
  if (!postcode) return '-';
  
  // Remove all spaces and convert to uppercase
  const clean = postcode.replace(/\s/g, '').toUpperCase();
  
  // Insert space before last 3 characters
  if (clean.length >= 5) {
    return `${clean.slice(0, -3)} ${clean.slice(-3)}`;
  }
  
  return postcode.toUpperCase();
};

/**
 * Parse a UK date string (DD/MM/YYYY) to Date object
 */
export const parseUKDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
  const year = parseInt(parts[2], 10);
  
  const date = new Date(year, month, day);
  
  // Validate the date is real
  if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
    return null;
  }
  
  return date;
};

/**
 * UK terminology mapping
 */
export const ukTerminology: Record<string, string> = {
  color: 'colour',
  colors: 'colours',
  center: 'centre',
  centers: 'centres',
  organization: 'organisation',
  organizations: 'organisations',
  organize: 'organise',
  organized: 'organised',
  analyze: 'analyse',
  analyzed: 'analysed',
  customize: 'customise',
  customized: 'customised',
  optimization: 'optimisation',
  optimize: 'optimise',
  optimized: 'optimised',
  license: 'licence', // noun form
  behavior: 'behaviour',
  behaviors: 'behaviours',
  favor: 'favour',
  favored: 'favoured',
  honor: 'honour',
  honored: 'honoured',
  labor: 'labour',
  catalog: 'catalogue',
  catalogs: 'catalogues',
  dialog: 'dialogue',
  dialogs: 'dialogues',
  check: 'cheque', // financial context
  checks: 'cheques',
  gray: 'grey',
  traveling: 'travelling',
  traveler: 'traveller',
  modeling: 'modelling',
  canceled: 'cancelled',
  labeling: 'labelling',
};

/**
 * Convert US English text to UK English
 */
export const toUKEnglish = (text: string): string => {
  let result = text;
  
  for (const [us, uk] of Object.entries(ukTerminology)) {
    // Case-insensitive replacement
    const regex = new RegExp(`\\b${us}\\b`, 'gi');
    result = result.replace(regex, (match) => {
      // Preserve case
      if (match[0] === match[0].toUpperCase()) {
        return uk.charAt(0).toUpperCase() + uk.slice(1);
      }
      return uk;
    });
  }
  
  return result;
};

/**
 * UK date format string for date pickers
 */
export const UK_DATE_FORMAT = 'dd/MM/yyyy';

/**
 * UK datetime format string
 */
export const UK_DATETIME_FORMAT = 'dd/MM/yyyy HH:mm';

/**
 * UK time format string (24-hour)
 */
export const UK_TIME_FORMAT = 'HH:mm';
